import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronLeft, FileCheck, Loader2 } from 'lucide-react';
import { API_BASE, DEFAULT_FC, FIELD_DEFAULTS, EMPTY_FORM } from './wizard/constants';
import { isValidIban, isValidPhone, isValidHouseNumber, blankToNull, normalizeHhmm, formForAnotherRegistration } from './wizard/utils';
import { availabilityReady } from '@/lib/availability';
import { formatApiError } from '@/lib/errors';
import { FullscreenShell, Stepper } from './wizard/shared';
import StepSubject from './wizard/StepSubject';
import StepStudent from './wizard/StepStudent';
import StepGuardian from './wizard/StepGuardian';
import StepSlot from './wizard/StepSlot';
import StepPayer from './wizard/StepPayer';
import StepSepa from './wizard/StepSepa';
import StepSummary from './wizard/StepSummary';
import { RegistrationSuccess } from './wizard/RegistrationSuccess';

export default function PublicRegistrationWizard() {
  const { tenantSlug } = useParams();
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState(null);

  const [stepIdx, setStepIdx] = useState(0);
  const [furthest, setFurthest] = useState(0);
  const [sectionOpen, setSectionOpen] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const submitLock = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/public/registrations/${encodeURIComponent(tenantSlug)}/config`);
        if (!cancelled) setConfig(r.data);
      } catch (e) {
        if (!cancelled) {
          setConfigError(e?.response?.data?.detail || 'Konfiguration konnte nicht geladen werden');
        }
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantSlug]);

  const fc = useMemo(() => {
    const raw = config?.form_config || {};
    return {
      steps: { ...DEFAULT_FC.steps, ...(raw.steps || {}) },
      texts: { ...DEFAULT_FC.texts, ...(raw.texts || {}) },
      fields: raw.fields || {},
      custom_fields: Array.isArray(raw.custom_fields) ? raw.custom_fields : [],
      verification: { phone_required: false, email_required: false, ...(raw.verification || {}) },
    };
  }, [config]);

  const F = useMemo(() => (key) => ({ ...FIELD_DEFAULTS[key], ...(fc.fields[key] || {}) }), [fc]);
  const T = (key) => fc.texts[key] || DEFAULT_FC.texts[key];

  const categoryVisible = F('student.category').visible;
  const categories = useMemo(() => config?.instrument_categories || [], [config]);
  const courseSubject = useMemo(
    () => categories.slice(1).some((c) => (c.items || []).includes(form.student.instrument)),
    [categories, form.student.instrument],
  );
  const subjectOptions = useMemo(() => {
    const raw = config?.subject_teaching_options?.[form.student.instrument] || [];
    return raw.map((o) => (typeof o === 'string' ? { name: o, price_text: '' } : o));
  }, [config, form.student.instrument]);

  const steps = useMemo(() => {
    const arr = [
      { key: 'subject', label: T('step_subject_title') },
      { key: 'payer', label: T('step_payer_title') },
    ];
    if (fc.steps.sepa) arr.push({ key: 'sepa', label: T('step_sepa_title') });
    arr.push({ key: 'student', label: T('step_student_title') });
    if (fc.steps.guardian) arr.push({ key: 'guardian', label: T('step_guardian_title') });
    const groupWaitlist = form.student.lesson_type === 'Gruppenunterricht' && form.group.priority === 'waitlist';
    if (fc.steps.slot && !groupWaitlist && !courseSubject) {
      const rawSlot = T('step_slot_title');
      const slotLabel = !rawSlot || rawSlot === 'Wunschtermin' || rawSlot === 'Wunschtermin auswählen'
        ? 'Verfügbarkeit'
        : rawSlot;
      arr.push({ key: 'slot', label: slotLabel });
    }
    arr.push({ key: 'summary', label: T('step_summary_title') });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fc, form.student.lesson_type, form.group.priority, courseSubject]);

  useEffect(() => {
    const last = Math.max(steps.length - 1, 0);
    if (stepIdx > last) setStepIdx(last);
    if (furthest > last) setFurthest(last);
  }, [steps, stepIdx, furthest]);

  const slotDuration = useMemo(() => {
    if (form.student.lesson_type === 'Gruppenunterricht' && form.group.priority === 'switch_20min') return 20;
    const m = (form.teaching_type || '').match(/(\d{2,3})\s*min/i);
    return m ? parseInt(m[1], 10) : 45;
  }, [form.student.lesson_type, form.group.priority, form.teaching_type]);

  const currentStepKey = steps[stepIdx]?.key;

  const setField = (section, key, value) => {
    setForm((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };
  const setStudent = (next) => setForm((prev) => ({
    ...prev,
    student: typeof next === 'function' ? next(prev.student) : next,
  }));
  const setRoot = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setCustom = (id, value) => setForm((prev) => ({ ...prev, custom: { ...prev.custom, [id]: value } }));

  const reqOk = (key, value) => {
    const spec = F(key);
    if (!spec.visible || !spec.required) return true;
    return !!(value && String(value).trim());
  };

  const [showErrors, setShowErrors] = useState(false);
  useEffect(() => { setShowErrors(false); }, [currentStepKey]);

  const missingFields = useMemo(() => {
    const miss = [];
    const add = (key, ok) => { if (!ok) miss.push(key); };
    const filled = (v) => !!(v && String(v).trim());
    const s = form.student;
    const p = form.payer;
    const sepa = form.sepa;
    const c = form.consents;
    const g = form.group;
    const customReq = (step) => fc.custom_fields
      .filter((cf) => cf.step === step && cf.required)
      .forEach((cf) => {
        const v = form.custom[cf.id];
        add(`custom.${cf.id}`, cf.type === 'checkbox' ? !!v : filled(v));
      });
    switch (currentStepKey) {
      case 'subject': {
        add('subject.instrument', !!s.instrument);
        if (s.instrument && !courseSubject && subjectOptions.length > 0) add('subject.teaching_type', !!form.teaching_type);
        if (s.lesson_type === 'Gruppenunterricht') {
          add('group.priority', !!g.priority);
          if (g.priority === 'add_students') {
            add('group.students', g.additional_students.length > 0
              && g.additional_students.every((as) => as.first_name.trim() && as.last_name.trim()));
          }
        }
        add('student.notes', reqOk('student.notes', s.notes));
        break;
      }
      case 'student': {
        add('student.first_name', filled(s.first_name));
        add('student.last_name', filled(s.last_name));
        add('student.birthdate', reqOk('student.birthdate', s.birth_year && s.birth_month && s.birth_day ? 'x' : ''));
        add('student.gender', reqOk('student.gender', s.gender));
        if (!s.address_same) {
          add('student.street', filled(s.street));
          add('student.house_number', isValidHouseNumber(s.house_number));
          add('student.postal_code', filled(s.postal_code));
          add('student.city', filled(s.city));
        }
        if (filled(s.phone)) add('student.phone', isValidPhone(s.phone));
        if (filled(s.email)) add('student.email', /.+@.+\..+/.test(s.email));
        customReq('student');
        break;
      }
      case 'guardian': {
        const gd = form.guardian;
        if (gd.same_as_payer) break;
        add('guardian.first_name', filled(gd.first_name));
        add('guardian.last_name', filled(gd.last_name));
        if (!gd.address_same) {
          add('guardian.street', filled(gd.street));
          add('guardian.house_number', isValidHouseNumber(gd.house_number));
          add('guardian.postal_code', filled(gd.postal_code));
          add('guardian.city', filled(gd.city));
        }
        if (filled(gd.phone)) add('guardian.phone', isValidPhone(gd.phone));
        if (filled(gd.email)) add('guardian.email', /.+@.+\..+/.test(gd.email));
        break;
      }
      case 'slot':
        add('slot', availabilityReady(form.availability));
        break;
      case 'payer': {
        add('payer.first_name', filled(p.first_name));
        add('payer.last_name', filled(p.last_name));
        add('payer.email', /.+@.+\..+/.test(p.email));
        const phoneSpec = F('payer.phone');
        if (phoneSpec.visible && (phoneSpec.required || filled(p.phone))) {
          add('payer.phone', filled(p.phone) && isValidPhone(p.phone));
        }
        add('payer.street', reqOk('payer.street', p.street));
        const hnSpec = F('payer.house_number');
        if (hnSpec.visible && (hnSpec.required || filled(p.house_number))) {
          add('payer.house_number', isValidHouseNumber(p.house_number));
        }
        add('payer.postal_code', reqOk('payer.postal_code', p.postal_code));
        add('payer.city', reqOk('payer.city', p.city));
        const verCfg = fc.verification || {};
        if (verCfg.phone_required) {
          add('payer.phone_verify', !!(form.phone_verification?.verified
            && form.phone_verification.value_raw === p.phone));
        }
        customReq('payer');
        break;
      }
      case 'sepa':
        add('sepa.iban', isValidIban(sepa.iban));
        add('sepa.mandate', sepa.mandate_acknowledged);
        if (sepa.different_holder) add('sepa.account_holder', filled(sepa.account_holder));
        break;
      case 'summary':
        add('consents.agb', c.agb);
        add('consents.datenschutz', c.datenschutz);
        add('consents.widerruf', c.widerruf);
        if (form.muensterland_card?.has_card) {
          add('muensterland_card.number', filled(form.muensterland_card.number));
        }
        break;
      default:
        break;
    }
    return miss;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepKey, form, fc, courseSubject, subjectOptions]);

  const canProceed = missingFields.length === 0;
  const invalid = (key) => showErrors && missingFields.includes(key);

  const submit = async () => {
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      const startTime = normalizeHhmm(form.preferred_slot?.start_time);
      const endTime = normalizeHhmm(form.preferred_slot?.end_time);
      const slot = form.preferred_slot && form.preferred_slot.teacher_id && form.preferred_slot.date && startTime && endTime
        ? {
            teacher_id: String(form.preferred_slot.teacher_id),
            date: form.preferred_slot.date,
            start_time: startTime,
            end_time: endTime,
            duration_minutes: Number(form.preferred_slot.duration_minutes || slotDuration) || 45,
          }
        : null;
      const isGroup = form.student.lesson_type === 'Gruppenunterricht';
      const slotStepShown = steps.some((st) => st.key === 'slot');
      const availability = slotStepShown
        ? (form.availability || [])
          .filter((w) => w.weekday && w.from && (!w.to || w.to > w.from))
          .map((w) => ({ weekday: w.weekday, from: w.from, to: w.to || null }))
        : null;
      const { birth_day, birth_month, birth_year, ...payerRest } = form.payer;
      const payerBirthdate = birth_year && birth_month && birth_day
        ? `${birth_year}-${String(birth_month).padStart(2, '0')}-${String(birth_day).padStart(2, '0')}`
        : null;
      const { birth_day: sD, birth_month: sM, birth_year: sY, ...studentRest } = form.student;
      delete studentRest.same_as_payer;
      const studentBirthdate = sY && sM && sD
        ? `${sY}-${String(sM).padStart(2, '0')}-${String(sD).padStart(2, '0')}`
        : null;
      if (studentRest.address_same) {
        studentRest.street = payerRest.street;
        studentRest.house_number = payerRest.house_number;
        studentRest.address_extra = payerRest.address_extra;
        studentRest.postal_code = payerRest.postal_code;
        studentRest.city = payerRest.city;
        studentRest.district = payerRest.district;
      }
      const { birth_day: gD, birth_month: gM, birth_year: gY, ...guardianRest } = form.guardian;
      const guardianBirthdate = gY && gM && gD
        ? `${gY}-${String(gM).padStart(2, '0')}-${String(gD).padStart(2, '0')}`
        : null;
      if (guardianRest.address_same) {
        guardianRest.street = payerRest.street;
        guardianRest.house_number = payerRest.house_number;
        guardianRest.address_extra = payerRest.address_extra;
        guardianRest.postal_code = payerRest.postal_code;
        guardianRest.city = payerRest.city;
        guardianRest.district = payerRest.district;
      }
      const body = {
        student: {
          ...studentRest,
          birthdate: studentBirthdate,
          category: form.student.category || null,
          lesson_type: form.student.lesson_type || null,
          salutation: studentRest.salutation || null,
          title: studentRest.title || null,
          gender: studentRest.gender || null,
          email: blankToNull(studentRest.email),
          phone: blankToNull(studentRest.phone),
        },
        payer: {
          ...payerRest,
          salutation: payerRest.salutation || null,
          title: payerRest.title || null,
          gender: payerRest.gender || null,
          email: blankToNull(payerRest.email),
          phone: blankToNull(payerRest.phone),
          birthdate: payerBirthdate,
          additional_phones: (payerRest.additional_phones || [])
            .map((ap) => ({ type: ap.type || 'Festnetz', number: (ap.number || '').trim() }))
            .filter((ap) => ap.number.length >= 3),
        },
        sepa: fc.steps.sepa
          ? {
              account_holder: form.sepa.different_holder
                ? form.sepa.account_holder.trim()
                : `${form.payer.first_name} ${form.payer.last_name}`.trim(),
              iban: form.sepa.iban.replace(/\s/g, ''),
              bic: blankToNull((form.sepa.bic || '').replace(/\s/g, '')),
              mandate_acknowledged: form.sepa.mandate_acknowledged,
            }
          : null,
        consents: form.consents,
        muensterland_card: {
          has_card: !!form.muensterland_card?.has_card,
          number: form.muensterland_card?.has_card
            ? String(form.muensterland_card.number || '').trim()
            : '',
        },
        preferred_slot: slotStepShown ? slot : null,
        availability,
        fallback_contact: false,
        custom_fields: form.custom,
        teaching_type: blankToNull(form.teaching_type),
        course_offer: form.course_offer?.id
          ? { id: form.course_offer.id, source: form.course_offer.source, title: form.course_offer.title }
          : null,
        location_id: blankToNull(form.location_id),
        phone_verification_id: (form.phone_verification?.verified
          && form.phone_verification.value_raw === form.payer.phone)
          ? form.phone_verification.id : null,
        guardian: fc.steps.guardian
          ? (guardianRest.same_as_payer
            ? { same_as_payer: true }
            : {
                ...guardianRest,
                salutation: guardianRest.salutation || null,
                title: guardianRest.title || null,
                gender: guardianRest.gender || null,
                email: blankToNull(guardianRest.email),
                phone: blankToNull(guardianRest.phone),
                birthdate: guardianBirthdate,
              })
          : null,
        group: isGroup && form.group.priority
          ? {
              priority: form.group.priority,
              additional_students: form.group.priority === 'add_students' ? form.group.additional_students : [],
            }
          : null,
      };
      const r = await axios.post(`${API_BASE}/public/registrations/${encodeURIComponent(tenantSlug)}`, body);
      setSubmitResult({ ok: true, message: r.data.message });
    } catch (e) {
      toast.error(formatApiError(e, 'Absenden fehlgeschlagen'));
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  if (loadingConfig) {
    return (
      <FullscreenShell>
        <div className="flex items-center gap-3 text-slate-600" data-testid="public-registration-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Konfiguration wird geladen …
        </div>
      </FullscreenShell>
    );
  }

  if (configError) {
    return (
      <FullscreenShell>
        <div className="max-w-md text-center" data-testid="public-registration-unavailable">
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Online-Anmeldung nicht verfügbar</h1>
          <p className="text-slate-600">{configError}</p>
        </div>
      </FullscreenShell>
    );
  }

  const startAnother = () => {
    setForm((prev) => formForAnotherRegistration(prev));
    setStepIdx(0);
    setFurthest(0);
    setSectionOpen(true);
    setShowErrors(false);
    setSubmitResult(null);
    window.scrollTo(0, 0);
  };

  if (submitResult?.ok) {
    return (
      <FullscreenShell tenantName={config?.tenant_name} logoUrl={config?.logo_url} slug={tenantSlug}>
        <RegistrationSuccess message={submitResult.message} onAnother={startAnother} />
      </FullscreenShell>
    );
  }

  return (
    <FullscreenShell tenantName={config?.tenant_name} logoUrl={config?.logo_url} slug={tenantSlug}>
      <div className="w-full max-w-3xl mx-auto" data-testid="public-registration-wizard">
        {config?.registration_intro && (
          <div className="mb-6 bg-white border border-slate-200 rounded-xl p-5 md:p-6 text-base md:text-lg text-slate-700 whitespace-pre-line leading-relaxed" data-testid="public-registration-intro">
            {config.registration_intro}
          </div>
        )}

        <Stepper
          steps={steps}
          stepIdx={stepIdx}
          furthest={furthest}
          open={sectionOpen}
          onToggle={(i) => {
            if (i > furthest) return;
            if (i === stepIdx) setSectionOpen((v) => !v);
            else { setStepIdx(i); setSectionOpen(true); }
          }}
          renderPanel={(step) => (
            <>
              {step.key === 'subject' && (
                <StepSubject
                  form={form}
                  invalid={invalid}
                  setField={setField}
                  setRoot={setRoot}
                  categories={categories}
                  categoryVisible={categoryVisible}
                  courseSubject={courseSubject}
                  subjectOptions={subjectOptions}
                  locations={config?.locations || []}
                  tenantSlug={tenantSlug}
                  F={F}
                  title={T('step_subject_title')}
                />
              )}
              {step.key === 'student' && (
                <StepStudent
                  form={form}
                  invalid={invalid}
                  setField={setField}
                  setStudent={setStudent}
                  setCustom={setCustom}
                  F={F}
                  title={T('step_student_title')}
                  customFields={fc.custom_fields.filter((cf) => cf.step === 'student')}
                />
              )}
              {step.key === 'guardian' && (
                <StepGuardian form={form} invalid={invalid} setField={setField} title={T('step_guardian_title')} />
              )}
              {step.key === 'slot' && (
                <StepSlot form={form} setRoot={setRoot} tenantSlug={tenantSlug} title={T('step_slot_title')} duration={slotDuration} />
              )}
              {step.key === 'payer' && (
                <StepPayer
                  form={form}
                  invalid={invalid}
                  setField={setField}
                  setCustom={setCustom}
                  setRoot={setRoot}
                  tenantSlug={tenantSlug}
                  phoneRequired={!!fc.verification.phone_required}
                  F={F}
                  title={T('step_payer_title')}
                  hint={T('step_payer_hint')}
                  customFields={fc.custom_fields.filter((cf) => cf.step === 'payer')}
                />
              )}
              {step.key === 'sepa' && (
                <StepSepa form={form} invalid={invalid} setField={setField} F={F} title={T('step_sepa_title')} tenantName={config?.tenant_name} />
              )}
              {step.key === 'summary' && (
                <StepSummary
                  form={form}
                  invalid={invalid}
                  config={config}
                  setField={setField}
                  fc={fc}
                  title={T('step_summary_title')}
                />
              )}

              <div className="flex items-center justify-between mt-8 gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setStepIdx((i) => Math.max(0, i - 1)); setSectionOpen(true); }}
                  disabled={stepIdx === 0 || submitting}
                  data-testid="public-registration-back-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Zurück
                </Button>
                <div className="flex flex-col items-end gap-2 min-w-0">
                  {step.key === 'summary' && T('submit_legal_hint') && (
                    <p className="text-xs md:text-sm text-slate-500 text-right max-w-md" data-testid="public-registration-legal-hint">
                      {T('submit_legal_hint')}
                    </p>
                  )}
                  {showErrors && !canProceed && (
                    <span className="text-sm text-red-600 font-medium truncate" data-testid="public-registration-required-hint">
                      Bitte alle erforderlichen Felder ausfüllen
                    </span>
                  )}
                  {stepIdx < steps.length - 1 ? (
                    <Button
                      onClick={() => {
                        if (!canProceed) { setShowErrors(true); return; }
                        setShowErrors(false);
                        setFurthest((f) => Math.max(f, stepIdx + 1));
                        setStepIdx((i) => Math.min(steps.length - 1, i + 1));
                        setSectionOpen(true);
                      }}
                      data-testid="public-registration-next-btn"
                    >
                      Weiter <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        if (!canProceed) { setShowErrors(true); return; }
                        submit();
                      }}
                      disabled={submitting}
                      data-testid="public-registration-submit-btn"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileCheck className="h-4 w-4 mr-2" />}
                      {T('submit_button')}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        />
      </div>
    </FullscreenShell>
  );
}
